import { Injectable, inject } from "@angular/core";
import { WarehouseApi } from "../Api/WarehouseApi";
import { WarehouseState } from "./WarehouseState";
import { WarehouseSearchRequest } from "../Dto/WarehouseSearchRequest";
import { CreateWarehouseRequest } from "../Dto/CreateWarehouseRequest";
import { Warehouse } from "../Dto/WarehouseModel";
import { createBackendError, getBackendErrorMessage } from "../../../Common/Utility/UtilityError";

@Injectable({ providedIn: "root" })
export class WarehouseEffects {
  private readonly warehouseApi = inject(WarehouseApi);
  private readonly warehouseState = inject(WarehouseState);

  async searchWarehouses(params: WarehouseSearchRequest = {}): Promise<void> {
    const mergedParams: WarehouseSearchRequest = {
      page: 1,
      limit: 10,
      ...params,
    };

    this.warehouseState.setLoading(true);
    this.warehouseState.setError(null);

    try {
      const response = await this.warehouseApi.searchWarehouses(mergedParams);
      this.warehouseState.setWarehouses(response.data, response.pagination);
      this.warehouseState.setSearchParams(mergedParams);
    } catch (exception: unknown) {
      const errorMessage = getBackendErrorMessage(exception);
      this.warehouseState.setError(errorMessage);
    } finally {
      this.warehouseState.setLoading(false);
    }
  }

  async deleteWarehouse(warehouseId: string): Promise<boolean> {
    this.warehouseState.setLoading(true);
    this.warehouseState.setError(null);

    try {
      await this.warehouseApi.deleteWarehouse(warehouseId);
      this.warehouseState.removeWarehouseFromState(warehouseId);
      return true;
    } catch (exception: unknown) {
      const errorMessage = getBackendErrorMessage(exception);
      this.warehouseState.setError(errorMessage);
      throw createBackendError(exception);
    } finally {
      this.warehouseState.setLoading(false);
    }
  }

  async createWarehouse(payload: CreateWarehouseRequest): Promise<Warehouse> {
    this.warehouseState.setLoading(true);
    this.warehouseState.setError(null);

    try {
      const createdWarehouse = await this.warehouseApi.createWarehouse(payload);
      this.warehouseState.addWarehouseToState(createdWarehouse);
      return createdWarehouse;
    } catch (exception: unknown) {
      const errorMessage = getBackendErrorMessage(exception);
      this.warehouseState.setError(errorMessage);
      throw createBackendError(exception);
    } finally {
      this.warehouseState.setLoading(false);
    }
  }

  async updateWarehouse(id: string, payload: Partial<CreateWarehouseRequest>): Promise<Warehouse> {
    this.warehouseState.setLoading(true);
    this.warehouseState.setError(null);

    try {
      const updatedWarehouse = await this.warehouseApi.updateWarehouse(id, payload);
      if (!updatedWarehouse) {
        throw new Error("Warehouse not found.");
      }
      this.warehouseState.updateWarehouseInState(updatedWarehouse);
      return updatedWarehouse;
    } catch (exception: unknown) {
      const errorMessage = getBackendErrorMessage(exception);
      this.warehouseState.setError(errorMessage);
      throw createBackendError(exception);
    } finally {
      this.warehouseState.setLoading(false);
    }
  }

  async getWarehouseById(id: string): Promise<Warehouse | null> {
    this.warehouseState.setLoading(true);
    this.warehouseState.setError(null);

    try {
      const warehouse = await this.warehouseApi.getWarehouseById(id);
      return warehouse;
    } catch (exception: unknown) {
      const errorMessage = getBackendErrorMessage(exception);
      this.warehouseState.setError(errorMessage);
      return null;
    } finally {
      this.warehouseState.setLoading(false);
    }
  }
}
